import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { FloorPlan } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getUserPlans, savePlan, deletePlan } from '../services/firestore';

// Reuse color scheme from App.tsx
const COLORS = {
  primary: '#fbf2ed', // Header color
  secondary: '#d0d7c9', // Button color
  accent: '#d0d7c9', // Accent color (using same as buttons)
  light: '#f8f9fa', // Light background
  dark: '#555555', // Dark text
  lightText: '#555555', // Updated for readability on light backgrounds
  border: '#dee2e6',
  hover: '#c2c9bc', // Darker button color for hover states
  success: '#28a745',
  error: '#dc3545',
  gray: '#6c757d',
  lightGray: '#e9ecef',
};

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
`;

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 28px;
  color: ${COLORS.dark};
  margin: 0;
`;

const Button = styled.button`
  padding: 10px 16px;
  background-color: ${COLORS.primary};
  color: #555555;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: ${COLORS.hover};
    transform: translateY(-2px);
  }
`;

const LogoutButton = styled(Button)`
  background-color: ${COLORS.gray};
  margin-left: 12px;

  &:hover {
    background-color: #5a6268;
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 24px;
  margin-top: 24px;
`;

const PlanCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s;
  display: flex;
  flex-direction: column;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
  }
`;

const PlanPreview = styled.div`
  background-color: ${COLORS.lightGray};
  height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.gray};
  border-bottom: 1px solid ${COLORS.border};
`;

const PlanInfo = styled.div`
  padding: 16px;
  flex-grow: 1;
`;

const PlanName = styled.h3`
  margin: 0 0 8px;
  font-size: 18px;
  color: ${COLORS.dark};
`;

const PlanDate = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${COLORS.gray};
`;

const PlanActions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
`;

const ActionButton = styled.button`
  padding: 8px 12px;
  background-color: transparent;
  color: #555555;
  border: 1px solid ${COLORS.primary};
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: ${COLORS.primary};
    color: #555555;
  }
`;

const DeleteButton = styled(ActionButton)`
  color: #555555;
  border-color: ${COLORS.error};

  &:hover {
    background-color: ${COLORS.error};
    color: #555555;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 64px 16px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const EmptyStateText = styled.p`
  font-size: 18px;
  color: ${COLORS.gray};
  margin-bottom: 24px;
`;

interface DashboardProps {
  onEditPlan: (plan: FloorPlan) => void;
}

const Dashboard: React.FC<DashboardProps> = ({ onEditPlan }) => {
  const [plans, setPlans] = useState<FloorPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const { currentUser, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    async function loadPlans() {
      if (currentUser) {
        try {
          const userPlans = await getUserPlans(currentUser.uid);
          setPlans(userPlans);
        } catch (error) {
          console.error('Error loading plans:', error);
        } finally {
          setLoading(false);
        }
      }
    }

    loadPlans();
  }, [currentUser]);

  const handleCreateNewPlan = async () => {
    if (!currentUser) return;

    // Default dimensions
    const defaultWidthFeet = 80; // 800px / 10 = 80ft
    const defaultHeightFeet = 60; // 600px / 10 = 60ft
    const FEET_TO_PIXELS = 10; // Same as in App.tsx

    const newPlan: FloorPlan = {
      id: uuidv4(),
      name: 'New Venue',
      size: {
        width: defaultWidthFeet * FEET_TO_PIXELS,
        height: defaultHeightFeet * FEET_TO_PIXELS
      },
      items: []
    };

    try {
      const planId = await savePlan(currentUser.uid, newPlan);
      newPlan.id = planId; // Update with the Firestore document ID
      onEditPlan(newPlan);
    } catch (error) {
      console.error('Error creating new plan:', error);
    }
  };

  const handleEditPlan = (plan: FloorPlan) => {
    onEditPlan(plan);
    navigate('/edit');
  };

  const handleDeletePlan = async (planId: string) => {
    if (window.confirm('Are you sure you want to delete this plan?')) {
      try {
        await deletePlan(planId);
        setPlans(plans.filter(plan => plan.id !== planId));
      } catch (error) {
        console.error('Error deleting plan:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardContainer>
      <Header>
        <Title>My Venue Plans</Title>
        <div>
          <Button onClick={handleCreateNewPlan}>Create New Plan</Button>
          <LogoutButton onClick={handleLogout}>Log Out</LogoutButton>
        </div>
      </Header>

      {plans.length === 0 ? (
        <EmptyState>
          <EmptyStateText>You don't have any venue plans yet.</EmptyStateText>
          <Button onClick={handleCreateNewPlan}>Create Your First Plan</Button>
        </EmptyState>
      ) : (
        <PlansGrid>
          {plans.map((plan) => (
            <PlanCard key={plan.id}>
              <PlanPreview>Venue Preview</PlanPreview>
              <PlanInfo>
                <PlanName>{plan.name}</PlanName>
                <PlanDate>
                  Last updated: {new Date(plan.updatedAt as any).toLocaleDateString()}
                </PlanDate>
                <PlanActions>
                  <ActionButton onClick={() => handleEditPlan(plan)}>
                    Edit Plan
                  </ActionButton>
                  <DeleteButton onClick={() => handleDeletePlan(plan.id)}>
                    Delete
                  </DeleteButton>
                </PlanActions>
              </PlanInfo>
            </PlanCard>
          ))}
        </PlansGrid>
      )}
    </DashboardContainer>
  );
};

export default Dashboard; 